import React, { forwardRef, useImperativeHandle } from 'react';
import { i18n } from 'src/common';
import {Card } from 'src/components';
import { InputText } from 'src/components/inputs/inputText.input';
import { InputPhoneNumber } from 'src/components/inputs/inputPhoneNumber.input';
import { FieldConfig, Form, FormRef } from './form';
import { Validation } from './validation';

export interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface CustomerFormProps {
  data: CustomerFormData;
  onSubmit: (values: CustomerFormData) => void;
}

export const CustomerForm = forwardRef<FormRef, CustomerFormProps>(({ data, onSubmit }, ref) => {
  const formRef = React.useRef<FormRef>(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formRef.current) {
        formRef.current.submitForm();
      }
    },
    getValues: () => formRef.current?.getValues?.() || {},
    validateForm: () => formRef.current?.validateForm?.() || false,
    setFieldValue: (fieldName: string, value: any, validation?: boolean) => {
      formRef.current?.setFieldValue?.(fieldName, value, validation);
    }
  }));

  const fields: FieldConfig<CustomerFormData>[] = [
    {
      name: 'firstName',
      label: i18n.t('components.forms.customer.firstName'),
      component: InputText,
      componentProps: {
        regex: /[^-a-zA-ZčćžšđČĆŽŠĐ]/gu
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.customer.firstNameRequired')
        }
      ] as Validation[],
    },
    {
      name: 'lastName',
      label: i18n.t('components.forms.customer.lastName'),
      component: InputText,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.customer.lastNameRequired')
        }
      ] as Validation[]
    },
    {
      name: 'email',
      label: i18n.t('components.forms.customer.email'),
      component: InputText,
      componentProps: {
        type: 'email'
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.customer.emailRequired')
        },
        {
          type: 'validMail',
          message: i18n.t('components.forms.customer.emailInvalid')
        }
      ] as Validation[]
    },
    {
      name: 'phoneNumber',
      label: i18n.t('components.forms.customer.phone'),
      component: InputPhoneNumber,
      formatValue: (values: CustomerFormData & { countryCode?: string; cleanPhoneNumber?: string }) =>
        values.phoneNumber ?? '',
      validations: [
        { type: 'required', message: i18n.t('common.validation.validPhoneNumber') },
        {
          type: 'validPhoneNumber',
          message: i18n.t('common.validation.validPhoneNumber'),
          params: { regionCode: 'countryCode' },
        },
      ] as Validation[],
    }
  ];

  const handleSubmit = (values: CustomerFormData & { countryCode?: string; cleanPhoneNumber?: string }) => {
    const convertedValues: CustomerFormData = {
      firstName: values.firstName || '',
      lastName: values.lastName || '',
      email: values.email || '',
      phoneNumber: values.phoneNumber || '',
    };
    onSubmit(convertedValues);
  };

  return (
    <Card style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 44,
      padding: 24,
      paddingTop: 32,
    }}>
      <Form
        ref={formRef}
        fields={fields}
        initialValues={data}
        onSubmit={handleSubmit}
        config={{ gap: 24 }}
      />
    </Card>
  );
});
